import { useCallback, useEffect, useRef, useState } from "react"

import {
  CalendarClock,
  Download,
  FileImage,
  FileText,
  Image,
  Loader2,
  Share2,
} from "@/components/icons"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DashboardSpec } from "../dashboard/catalog"
import type { DashboardStateStore } from "../state/dashboard-state"

import { QueryErrorBoundary } from "@/components/query-error-boundary"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { useDashStore } from "@/stores/dash-store"

import { BookmarkMenu } from "./bookmark-menu"
import { DashboardBreadcrumb } from "./dashboard-breadcrumb"
import { DashboardScheduleDialog } from "./dashboard-schedule-dialog"
import { DashboardShareDialog } from "./dashboard-share-dialog"
import { DashboardToolbar } from "./dashboard-toolbar"
import { SaveDashboardDialog } from "./save-dashboard-dialog"
import { TileEditPopover } from "./tile-edit-popover"
import { DashboardRenderer } from "../dashboard/dashboard-renderer"
import { useDashboardExport } from "../hooks/use-dashboard-export"
import { useDashboardLastOpened } from "../hooks/use-dashboard-last-opened"
import { useMarkDashboardViewed } from "../hooks/use-dashboard-participants"
import { useDashboards, useUpdateDashboard } from "../hooks/use-dashboards"
import { getFlatTiles } from "../utils/tile-helpers"
import { reconcileDashboardQueryBindings } from "../utils/reconcile-query-bindings"
import { applyAllAutoFixableSpecIssues } from "../utils/spec-fixers"

type DashboardSidebarContentProps = {
  sessionType: string
  onBackToList?: () => void
  onTogglePanel?: () => void
  onRefresh: (options?: { allowAiRefresh?: boolean }) => Promise<void>
  onEditWithAI?: (tileIndex: number, tileTitle: string, description?: string) => void
  /**
   * Receives the user's natural-language description from the
   * "+ Add tile" slot plus the insertion index. Wired through to
   * the chat composer so the agent emits a yaml-edit fence inserting
   * one tile at the requested position. When omitted, the slot stays
   * hidden — the renderer doesn't surface an affordance the host can't
   * service.
   */
  onAddTile?: (description: string, insertionIndex: number) => void
  /** Receives a getter for the current dashboard-state snapshot. */
  onStateReady?: (getState: () => Record<string, unknown>) => void
  /** Receives the live state store. Bookmarks need this to apply
   *  saved snapshots back to the dashboard. */

  onStoreReady?: (store: import("../state/dashboard-state").DashboardStateStore) => void
}

export function DashboardSidebarContent({
  sessionType,
  onBackToList,
  onTogglePanel,
  onRefresh,
  onEditWithAI,
  onAddTile,
  onStateReady,
  onStoreReady,
}: DashboardSidebarContentProps) {
  const { t } = useTranslation()
  const explorerStore = useDashStore(sessionType)

  const currentDashboardSpec = explorerStore((s) => s.currentDashboardSpec)
  const selectedTileIndex = explorerStore((s) => s.selectedTileIndex)
  const setSelectedTileIndex = explorerStore((s) => s.setSelectedTileIndex)
  const setDashboardSpec = explorerStore((s) => s.setDashboardSpec)
  const updateOnLoad = explorerStore((s) => s.updateOnLoad)
  const setUpdateOnLoad = explorerStore((s) => s.setUpdateOnLoad)
  const lastUpdated = explorerStore((s) => s.lastUpdated)
  const currentDashboardId = explorerStore((s) => s.currentDashboardId)
  // Quality-review fields persisted on the dashboard doc. The page-level
  // load callback hydrates these via loadDashboard's options; we read
  // them straight from the store so the banner can render the latest
  // score chip + sparkline without refetching. Cast via unknown to dodge
  // the loose typing in the store — the QualityReviewBanner asserts
  // the shape at render time.
  const currentDashboardQualityReview = explorerStore(
    (s) => s.currentDashboardQualityReview
  ) as ReturnType<() => import("../services/dashboard-service").QualityReview | null>
  const currentDashboardQualityHistory = explorerStore(
    (s) => s.currentDashboardQualityHistory
  ) as unknown as import("../services/dashboard-service").QualityHistoryEntry[]
  const setCurrentDashboardId = explorerStore((s) => s.setCurrentDashboardId)
  const setCurrentDashboardQualityReview = explorerStore((s) => s.setCurrentDashboardQualityReview)
  const setCurrentDashboardQualityHistory = explorerStore(
    (s) => s.setCurrentDashboardQualityHistory
  )
  const currentDashboardTitle = explorerStore((s) => s.currentDashboardTitle)
  const setCurrentDashboardTitle = explorerStore((s) => s.setCurrentDashboardTitle)
  const currentDashboardType = explorerStore((s) => s.currentDashboardType)
  const setCurrentDashboardType = explorerStore((s) => s.setCurrentDashboardType)
  const setDashboardMode = explorerStore((s) => s.setDashboardMode)
  const dashboardMode = explorerStore((s) => s.dashboardMode)
  const isRefreshing = explorerStore((s) => s.isDashboardRefreshing)

  const isFocused = useContentSidebarStore((s) => s.isFocused)
  const toggleFocus = useContentSidebarStore((s) => s.toggleFocus)

  // Permission: same pattern as workflow editor + the new role layer
  // from sharing. For company dashboards admins still gate via
  // is_prompt_admin. For personal dashboards we now also respect the
  // role surfaced by the backend (reader can't save).
  const isDashboardAdmin = SettingsService.getInstance().settings.user_policy.is_prompt_admin
  const updateDashboard = useUpdateDashboard()
  const { data: dashboards = [] } = useDashboards()

  const currentDashboard = currentDashboardId
    ? dashboards.find((d) => d.id === currentDashboardId)
    : undefined
  const currentDashboardRole = currentDashboard?.currentUserRole
  // Reader can browse + refresh but not save. Undefined role means
  // we don't have role info yet (e.g. brand-new dashboard before
  // first save, or a legacy doc whose response predates the sharing
  // patch) — preserve old behaviour by allowing edits in that case.
  const hasEditPermission =
    !currentDashboardId ||
    (currentDashboardType === "personal" && currentDashboardRole !== "reader") ||
    isDashboardAdmin
  // Only owners/admins on a saved personal dashboard can manage
  const canEdit = hasEditPermission && dashboardMode === "chat"
  // participants. Company dashboards stay tenant-wide via the
  // existing admin-group check and don't expose the share dialog.
  const canShareDashboard =
    !!currentDashboardId &&
    currentDashboardType === "personal" &&
    (currentDashboardRole === "owner" ||
      currentDashboardRole === "admin" ||
      // Legacy personal dashboard with no role info means the caller
      // is the owner (otherwise list_for_user would have skipped it).
      currentDashboardRole === undefined)

  const [showShareDialog, setShowShareDialog] = useState(false)
  const autoRepairingSpecRef = useRef<string | null>(null)
  const isChatStreaming = explorerStore((state) => {
    const sessionId = state.currentSessionId
    if (!sessionId || !(sessionId in state.sessionStates)) return false
    return state.sessionStates[sessionId].isStreaming
  })

  // Deterministic validator issues do not need a user decision. Repair them
  // as soon as a spec enters the runtime, update the renderer immediately,
  // and persist the cleaned spec for editors so the same issue is not back
  // on the next open. applyAll... re-validates between removals, which also
  // handles adjacent unknown children without an index-shift skip.
  useEffect(() => {
    if (!currentDashboardSpec || isChatStreaming) return
    const repaired = applyAllAutoFixableSpecIssues(currentDashboardSpec)
    if (!repaired.changed) return

    const runtimeSpec = repaired.spec
    setDashboardSpec(runtimeSpec)
    setSelectedTileIndex(null)

    if (!currentDashboardId || !hasEditPermission) return
    const persistedSpec = runtimeSpec
    const repairKey = `${currentDashboardId}:${JSON.stringify(persistedSpec)}`
    if (autoRepairingSpecRef.current === repairKey) return
    autoRepairingSpecRef.current = repairKey

    void updateDashboard
      .mutateAsync({
        dashboardId: currentDashboardId,
        payload: {
          spec: persistedSpec as Record<string, unknown>,
          type: currentDashboardType,
        },
      })
      .catch((error) => {
        logger.warn("Failed to persist automatic dashboard spec repair", error as Error)
      })
      .finally(() => {
        if (autoRepairingSpecRef.current === repairKey) autoRepairingSpecRef.current = null
      })
  }, [
    currentDashboardId,
    currentDashboardSpec,
    currentDashboardType,
    hasEditPermission,
    isChatStreaming,
    setDashboardSpec,
    setSelectedTileIndex,
    updateDashboard,
  ])
  const [showScheduleDialog, setShowScheduleDialog] = useState(false)

  // Capture the live state store so the bookmark menu can both read
  // (getState) and write (initDashState — replaces the full snapshot).
  // Wrap the parent's onStoreReady so dash-page still sees the same
  // ref it would have without bookmarks.
  const storeRef = useRef<DashboardStateStore | null>(null)
  const handleStoreReady = useCallback(
    (store: DashboardStateStore) => {
      storeRef.current = store
      onStoreReady?.(store)
    },
    [onStoreReady]
  )

  // Mark the invitation notification as viewed the first time the user
  // opens a shared dashboard they were invited to. Tracked per
  // dashboard id so re-opening doesn't fire redundant POSTs and we
  // don't race when the list response is still hydrating.
  const markViewed = useMarkDashboardViewed()
  const markedDashboardRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentDashboardId) return
    if (!currentDashboard?.hasUnviewedNotification) return
    if (markedDashboardRef.current === currentDashboardId) return
    markedDashboardRef.current = currentDashboardId
    markViewed.mutate(currentDashboardId)
  }, [currentDashboardId, currentDashboard?.hasUnviewedNotification, markViewed])

  const tileRefMap = useRef(new Map<number, HTMLDivElement>())
  const dashboardContainerRef = useRef<HTMLDivElement>(null)
  const { exportAsPng, exportAsSvg, exportAsPdf, isExporting } = useDashboardExport({
    containerRef: dashboardContainerRef,
    title: currentDashboardTitle,
  })

  const [showSaveDialog, setShowSaveDialog] = useState(false)

  // §G — restore last-opened dashboard on mount, persist on every change.
  // Auto-restore happens at most once per session, gated by `didRestoreRef`,
  // so the user can navigate to the empty list deliberately without it
  // snapping back. Skipped entirely when another source (URL, deeplink)
  // has already set currentDashboardId.
  const { lastOpenedId, setLastOpenedId } = useDashboardLastOpened()
  const didRestoreRef = useRef(false)
  useEffect(() => {
    if (didRestoreRef.current) return
    if (lastOpenedId === undefined) return // still loading
    didRestoreRef.current = true
    // Critical: never restore lastOpenedId while the user is composing
    // a brand-new dashboard via chat. Otherwise handleSave's PUT branch
    // sees the restored ID + the new chat-generated spec and silently
    // overwrites the previously-open dashboard's spec with the new one.
    // Reproduced 2026-05-12: SHL Klubbar Benchmark got clobbered by a
    // test dashboard because this effect re-applied the stale ID after
    // handleCreateNewDashboard had explicitly cleared it.
    if (dashboardMode === "chat") return
    if (!currentDashboardId && lastOpenedId) {
      setCurrentDashboardId(lastOpenedId)
    }
  }, [currentDashboardId, lastOpenedId, setCurrentDashboardId, dashboardMode])

  // Per-tile refresh in tile-binding-badge dispatches a custom event
  // with the fresh dashboard envelope. Listen here so the deep tree
  // doesn't need a threaded callback. Reload was the previous
  // mechanism — it dropped the user to landing because the open
  // dashboard isn't deep-linked in the URL.
  useEffect(() => {
    function handle(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { spec?: Record<string, unknown>; query_bindings?: unknown[] }
        | undefined
      if (!detail?.spec || !currentDashboardId) return
      const storedSpec = detail.spec as DashboardSpec
      const runtimeSpec: DashboardSpec = {
        ...storedSpec,
        queryBindings:
          (detail.query_bindings as DashboardSpec["queryBindings"]) ?? storedSpec.queryBindings,
      }
      setDashboardSpec(runtimeSpec)
    }
    window.addEventListener("dash:dashboard-refreshed", handle)
    return () => window.removeEventListener("dash:dashboard-refreshed", handle)
  }, [currentDashboardId, setDashboardSpec])

  useEffect(() => {
    // Persist whenever the active dashboard changes — including clearing
    // (currentDashboardId === null on the empty landing). The hook
    // de-duplicates so we don't pay for no-ops.
    if (lastOpenedId === undefined) return // still loading; don't overwrite
    setLastOpenedId(currentDashboardId ?? null)
  }, [currentDashboardId, lastOpenedId, setLastOpenedId])

  // Debounced title save
  const titleSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (titleSaveTimerRef.current) clearTimeout(titleSaveTimerRef.current)
    }
  }, [])
  const handleTitleChange = useCallback(
    (title: string) => {
      setCurrentDashboardTitle(title)
      const nextSpec = currentDashboardSpec ? { ...currentDashboardSpec, title } : null
      if (nextSpec) setDashboardSpec(nextSpec)
      if (!currentDashboardId) return
      if (titleSaveTimerRef.current) clearTimeout(titleSaveTimerRef.current)
      titleSaveTimerRef.current = setTimeout(() => {
        void updateDashboard
          .mutateAsync({
            dashboardId: currentDashboardId,
            payload: {
              title,
              type: currentDashboardType,
            },
          })
          .catch((err) => {
            logger.error("Debounced title save failed", err)
          })
      }, 800)
    },
    [
      currentDashboardId,
      currentDashboardSpec,
      currentDashboardType,
      setCurrentDashboardTitle,
      setDashboardSpec,
      updateDashboard,
    ]
  )

  // Type change with immediate save
  const handleTypeChange = useCallback(
    async (newType: "personal" | "company") => {
      const previousType = currentDashboardType
      setCurrentDashboardType(newType)
      if (currentDashboardId) {
        try {
          await updateDashboard.mutateAsync({
            dashboardId: currentDashboardId,
            payload: { type: newType },
          })
        } catch {
          setCurrentDashboardType(previousType)
        }
      }
    },
    [currentDashboardId, currentDashboardType, setCurrentDashboardType, updateDashboard]
  )

  const handleRefresh = useCallback(async () => {
    await onRefresh({ allowAiRefresh: hasEditPermission })
  }, [hasEditPermission, onRefresh])

  // Auto-save update_on_load toggle to backend
  const handleUpdateOnLoadChange = useCallback(
    async (enabled: boolean) => {
      const previous = updateOnLoad
      setUpdateOnLoad(enabled)
      if (currentDashboardId) {
        try {
          await updateDashboard.mutateAsync({
            dashboardId: currentDashboardId,
            payload: { update_on_load: enabled },
          })
        } catch {
          setUpdateOnLoad(previous)
        }
      }
    },
    [currentDashboardId, updateOnLoad, setUpdateOnLoad, updateDashboard]
  )

  // Quick save for existing dashboards. Defensive check: when the
  // store still holds a stale `currentDashboardId` from a previous
  // dashboard but the spec is brand-new (e.g. chat just generated one
  // and lastOpenedId restored the old ID), force the save dialog so
  // the new spec gets POSTed as a NEW dashboard instead of
  // overwriting the previously-open one. Detected via
  // dashboardSessionId mismatch — the spec carries the chat session
  // it was generated in, and a saved dashboard's spec retains the
  // session it was originally authored from. A mismatch means the
  // user is mid-creating-new, not editing the loaded one.
  const handleSave = useCallback(async () => {
    if (currentDashboardId && currentDashboardSpec) {
      const persistedDoc = dashboards.find((d) => d.id === currentDashboardId)
      const persistedSession = (persistedDoc?.spec as { dashboardSessionId?: string } | undefined)
        ?.dashboardSessionId
      const currentSession = (currentDashboardSpec as { dashboardSessionId?: string } | undefined)
        ?.dashboardSessionId
      if (persistedSession && currentSession && persistedSession !== currentSession) {
        // Stale ID — treat as new save to avoid overwriting another doc.
        setShowSaveDialog(true)
        return
      }
      await updateDashboard.mutateAsync({
        dashboardId: currentDashboardId,
        payload: {
          spec: currentDashboardSpec as Record<string, unknown>,
          type: currentDashboardType,
          replace_query_bindings: true,
        },
      })
      toast.success(t("dash.dashboard.auto_saved"))
    } else {
      setShowSaveDialog(true)
    }
  }, [
    currentDashboardId,
    currentDashboardSpec,
    currentDashboardType,
    updateDashboard,
    dashboards,
    t,
  ])

  // Toggle: focused → chat mode + minimize, not focused → maximize
  const handleEditWithAI = useCallback(() => {
    if (dashboardMode === "view") {
      setDashboardMode("chat")
      if (isFocused) toggleFocus()
      return
    }
    toggleFocus()
  }, [dashboardMode, setDashboardMode, isFocused, toggleFocus])

  const handleSpecChange = useCallback(
    (nextSpec: DashboardSpec) => {
      setDashboardSpec(
        currentDashboardSpec
          ? reconcileDashboardQueryBindings(currentDashboardSpec, nextSpec)
          : nextSpec
      )
    },
    [currentDashboardSpec, setDashboardSpec]
  )

  const handleSelectTile = useCallback(
    (index: number) => {
      // Readers can't open the per-tile edit popover or AI-edit affordance.
      // Suppress selection rather than rendering a disabled state — tiles
      // should just look static when you can't edit.
      if (!canEdit) return
      setSelectedTileIndex(index === -1 ? null : index)
    },
    [canEdit, setSelectedTileIndex]
  )

  const toolbarActions = (
    <div className="flex items-center gap-1">
      {currentDashboardId && (
        <BookmarkMenu
          dashboardId={currentDashboardId}
          getState={() => storeRef.current?.getState() ?? {}}
          onApply={(snapshot) => {
            // initDashState wipes the existing state and replaces it
            // with the snapshot — which is what "apply view" should
            // mean. Per-binding auto-refresh picks the change up.
            storeRef.current?.initDashState(snapshot)
          }}
        />
      )}
      {canShareDashboard && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t("dash.sharing.share", { defaultValue: "Share dashboard" })}
              className="h-7 w-7 shrink-0 p-0"
              size="icon"
              variant="ghost"
              onClick={() => setShowShareDialog(true)}
            >
              <Share2 className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("dash.sharing.share", { defaultValue: "Share dashboard" })}
          </TooltipContent>
        </Tooltip>
      )}
      {canShareDashboard && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t("dash.schedules.aria", { defaultValue: "Email schedules" })}
              className="h-7 w-7 shrink-0 p-0"
              data-testid="dashboard-schedule-trigger"
              size="icon"
              variant="ghost"
              onClick={() => setShowScheduleDialog(true)}
            >
              <CalendarClock className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("dash.schedules.tooltip", { defaultValue: "Email schedules" })}
          </TooltipContent>
        </Tooltip>
      )}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t("dash.dashboard.export")}
                className="h-7 w-7 shrink-0 p-0"
                disabled={isExporting || !currentDashboardSpec}
                size="icon"
                variant="ghost"
              >
                {isExporting ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Download className="size-3.5" />
                )}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t("dash.dashboard.export")}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={isExporting} onClick={exportAsPng}>
            <Image className="mr-2 size-4" />
            {t("dash.dashboard.export_png")}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isExporting} onClick={exportAsSvg}>
            <FileImage className="mr-2 size-4" />
            {t("dash.dashboard.export_svg")}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isExporting} onClick={exportAsPdf}>
            <FileText className="mr-2 size-4" />
            {t("dash.dashboard.export_pdf")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  return (
    <div className="flex h-full flex-col">
      {onBackToList && (
        <div className="border-border bg-card border-b px-4 py-1.5">
          <DashboardBreadcrumb
            dashboardTitle={
              currentDashboardTitle || currentDashboardSpec?.title || t("dash.dashboard.untitled")
            }
            onBackToList={onBackToList}
            onTogglePanel={onTogglePanel}
          />
        </div>
      )}
      <DashboardToolbar
        actions={toolbarActions}
        canEdit={canEdit}
        currentDashboardId={currentDashboardId}
        dashboardTitle={currentDashboardTitle}
        dashboardType={currentDashboardType}
        hasDashboardSpec={!!currentDashboardSpec}
        isDashboardAdmin={isDashboardAdmin}
        isFocused={dashboardMode === "view" || isFocused}
        isRefreshing={isRefreshing}
        lastUpdated={lastUpdated}
        updateOnLoad={updateOnLoad}
        canRefresh={
          !!currentDashboardSpec?.queryBindings?.length ||
          (hasEditPermission && !!currentDashboardSpec?.sourcePrompt)
        }
        hasSqlBindings={
          currentDashboardId ? !!currentDashboardSpec?.queryBindings?.length : undefined
        }
        onEditWithAI={hasEditPermission ? handleEditWithAI : undefined}
        onRefresh={handleRefresh}
        onSave={handleSave}
        onTitleChange={handleTitleChange}
        onTypeChange={handleTypeChange}
        onUpdateOnLoadChange={handleUpdateOnLoadChange}
      />
      <div aria-live="polite" className="sr-only">
        {isRefreshing ? t("dash.dashboard.refreshing") : ""}
      </div>
      {isRefreshing && (
        <div className="border-border bg-muted/40 text-muted-foreground flex items-center gap-2 border-b px-4 py-2 text-xs">
          <Loader2 className="size-3.5 animate-spin" />
          {t("dash.dashboard.refreshing")}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4">
        <div ref={dashboardContainerRef}>
          <QueryErrorBoundary>
            {currentDashboardSpec &&
              canEdit &&
              selectedTileIndex !== null &&
              selectedTileIndex >= 0 &&
              selectedTileIndex < getFlatTiles(currentDashboardSpec.children).length && (
                <TileEditPopover
                  anchorRef={tileRefMap.current.get(selectedTileIndex)}
                  selectedTileIndex={selectedTileIndex}
                  spec={currentDashboardSpec}
                  onDeselect={() => setSelectedTileIndex(null)}
                  onSpecChange={handleSpecChange}
                  // selectedTileIndex is a flat index from getFlatTiles(). The
                  // page-level handler converts the flat index into a JSON-Pointer
                  // element path via resolveTilePathByFlatIndex so the agent's
                  // yaml-patch targets the right nested node — works for Grid,
                  // Row, Tabs, Accordion. The legacy no-description branch still
                  // uses the flat index as a label for the composer hint.
                  onEditWithAI={(description) => {
                    if (onEditWithAI) {
                      const el = getFlatTiles(currentDashboardSpec.children).at(selectedTileIndex)
                        ?.element as { title?: string } | undefined
                      onEditWithAI(selectedTileIndex, el?.title ?? "", description)
                    }
                  }}
                >
                  <></>
                </TileEditPopover>
              )}
            <DashboardRenderer
              canEdit={canEdit}
              dashboardId={currentDashboardId ?? undefined}
              initialQualityReview={currentDashboardQualityReview}
              isRefreshing={isRefreshing}
              qualityHistory={currentDashboardQualityHistory}
              selectedTileIndex={selectedTileIndex}
              spec={currentDashboardSpec}
              tileRefMap={tileRefMap.current}
              onAddTile={onAddTile}
              onAutoRefresh={() => onRefresh({ allowAiRefresh: hasEditPermission })}
              onSelectTile={handleSelectTile}
              onSpecChange={handleSpecChange}
              onStateReady={onStateReady}
              onStoreReady={handleStoreReady}
              onDashboardRefreshed={(updated) => {
                // Mirrors handleDashboardClick in dashboards-folder.tsx:
                // merge query_bindings onto the runtime spec so
                // BindingsProvider sees them, then push everything into
                // the store. No page reload — that would drop us to the
                // landing page because the open dashboard isn't
                // deep-linked in the URL.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                const storedSpec = (updated.spec ?? {}) as DashboardSpec
                const runtimeSpec: DashboardSpec = {
                  ...storedSpec,
                  queryBindings: updated.query_bindings ?? storedSpec.queryBindings,
                }
                // Keep the current edit/view mode. loadDashboard is only for
                // navigation and intentionally forces view mode; using it for
                // an in-place AI fix made the fresh result look stale.
                setDashboardSpec(runtimeSpec)
                setCurrentDashboardId(updated.id)
                setCurrentDashboardTitle(updated.title)
                setCurrentDashboardType(updated.type)
                setUpdateOnLoad(updated.update_on_load ?? false)
                setCurrentDashboardQualityReview(updated.quality_review ?? null)
                setCurrentDashboardQualityHistory(updated.quality_history ?? [])
              }}
            />
          </QueryErrorBoundary>
        </div>
      </div>

      <SaveDashboardDialog
        dashboardId={currentDashboardId}
        open={showSaveDialog}
        spec={currentDashboardSpec}
        onOpenChange={setShowSaveDialog}
        onRefreshedSpec={setDashboardSpec}
        onDashboardCreated={(id, title, type) => {
          setCurrentDashboardId(id)
          setCurrentDashboardTitle(title)
          setCurrentDashboardType(type)
        }}
      />

      <DashboardShareDialog
        dashboard={currentDashboard}
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        onLeave={() => {
          // When the current user leaves a shared dashboard they lose
          // access — clear the open dashboard so the empty-landing state
          // takes over rather than 404'ing on the next list refresh.
          setCurrentDashboardId(null)
        }}
      />

      {currentDashboardId && (
        <DashboardScheduleDialog
          dashboardId={currentDashboardId}
          open={showScheduleDialog}
          onOpenChange={setShowScheduleDialog}
        />
      )}
    </div>
  )
}
